import { DateTime } from '@ksyos/date-time';
import { Card, Form, FormButtonRow, FormGroup, FormParagraph, IconButton } from '@ksyos/design-system';
import { pick } from 'lodash';
import { useEffect, useState } from 'react';
import type { IOrganizationKsyosAccount } from '@frontend/components/interfaces/organizationKsyosAccount';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { organizationKsyosAccountUpdateSchema } from '@common/validation/organization';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import { InputTriState } from '../../../elements/common';
import { FormContextProvider } from '../../../elements/formContext';
import { StopRightsModal } from '../../../elements/StopRightsModal';
import { CancelButton } from '../../../userDataManagement/CancelButton';
import { FormattedDate } from '../../../userDataManagement/FormattedDate';
import { InputRadioGroup } from '../../../userDataManagement/InputRadioGroup';
import { SaveButton } from '../../../userDataManagement/SaveButton';

interface IOrganizationKsyosAccountProps {
    organizationId: string;
}

export function OrganizationKsyosAccount(props: Readonly<IOrganizationKsyosAccountProps>) {
    const endDate = DateTime.now();
    const [canEdit, setCanEdit] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [originalOrganizationKsyosAccount, setOriginalOrganizationKsyosAccount] =
        useState<IOrganizationKsyosAccount>();
    const [organizationKsyosAccount, setOrganizationKsyosAccount] = useState<IOrganizationKsyosAccount>();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    assertHasAuthorization(ManagerAuthorization.organizationRead);

    useEffect(() => {
        loadOrganizationKsyosAccount(props.organizationId);
    }, [props.organizationId]);

    if (!organizationKsyosAccount) {
        return null;
    }

    return (
        <FormContextProvider
            value={{
                placeholderGroupName: 'ksyosAccount',
                item: organizationKsyosAccount,
                errors: errors,
                onChange: async (key, value) => handleChange(key, value),
                onDoValidate: async (key) => validateOne(key),
            }}
        >
            <Card>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        canEdit && !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={translateKey('profile.ksyosAccount')}
                />
                <Card.Body>
                    <Form submitOnEnter={false}>
                        {renderStatus(organizationKsyosAccount.isActive)}

                        <FormGroup label={translateKey('common.startingDate')}>
                            <FormattedDate value={organizationKsyosAccount.startDate} />
                        </FormGroup>

                        <FormParagraphFormGroup label={translateKey('organization.txcsId')}>
                            {organizationKsyosAccount.txcsId}
                        </FormParagraphFormGroup>

                        {renderHighPrivacy(organizationKsyosAccount.isHighPrivacy)}

                        {renderIsMijnKsyosActive(organizationKsyosAccount.isMijnKsyosActive)}

                        {renderAreContactsAndTasksEnabled(organizationKsyosAccount.areContactsAndTasksEnabled)}

                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => handleCancel()}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
            </Card>

            {showConfirmationModal && (
                <StopRightsModal
                    alertMessages={[translateKey('serviceRightStopModal.warningOrganization')]}
                    bodyText={translateKey('serviceRightStopModal.bodyOrganization')}
                    confirmText={translateKey('profile.archive')}
                    endDate={endDate.format('DD-MM-YYYY')}
                    onClose={() => setShowConfirmationModal(false)}
                    onStopRight={async () => {
                        await handleSubmit();
                    }}
                    title={translateKey('serviceRightStopModal.titleMakeOrganizationInactive')}
                />
            )}
        </FormContextProvider>
    );

    function renderHighPrivacy(isHighPrivacy: boolean) {
        const isEditMode = canEdit && isEditing;
        if (isEditMode) {
            return (
                <InputRadioGroup
                    fieldKey="isHighPrivacy"
                    label="organization.highPrivacyLevel"
                    radioItems={[
                        { id: 'on', label: translateKey('common.on'), value: true },
                        { id: 'off', label: translateKey('common.off'), value: false },
                    ]}
                    tooltipText={translateKey('organization.dossierNotAccessible')}
                />
            );
        } else {
            return (
                <FormGroup
                    label={translateKey('organization.highPrivacyLevel')}
                    tooltipText={translateKey('organization.dossierNotAccessible')}
                >
                    <FormParagraph>
                        {isHighPrivacy ? translateKey('common.on') : translateKey('common.off')}
                    </FormParagraph>
                </FormGroup>
            );
        }
    }

    function renderIsMijnKsyosActive(isMijnKsyosActive: boolean) {
        const isEditMode = canEdit && isEditing;
        if (isEditMode) {
            return (
                <InputRadioGroup
                    fieldKey="isMijnKsyosActive"
                    label="organization.isMijnKsyosActive"
                    radioItems={[
                        { id: 'isMijnKsyosActiveOn', label: translateKey('common.on'), value: true },
                        { id: 'isMijnKsyosActiveOff', label: translateKey('common.off'), value: false },
                    ]}
                    tooltipText={translateKey('organization.isMijnKsyosActiveTooltip')}
                />
            );
        } else {
            return (
                <FormGroup
                    label={translateKey('organization.isMijnKsyosActive')}
                    tooltipText={translateKey('organization.isMijnKsyosActiveTooltip')}
                >
                    <FormParagraph>
                        {isMijnKsyosActive ? translateKey('common.on') : translateKey('common.off')}
                    </FormParagraph>
                </FormGroup>
            );
        }
    }

    function renderAreContactsAndTasksEnabled(areContactsAndTasksEnabled: boolean) {
        const isEditMode = canEdit && isEditing;
        if (isEditMode) {
            return (
                <FormGroup
                    label={translateKey('organization.areContactsAndTasksEnabled')}
                    tooltipText={translateKey('organization.areContactsAndTasksEnabledTooltip')}
                >
                    <InputTriState
                        error={getValidationErrorTranslation(errors.get('areContactsAndTasksEnabled'))}
                        fieldKey="areContactsAndTasksEnabled"
                        name="areContactsAndTasksEnabled"
                        notNullable
                        onChange={async (value) => handleChange('areContactsAndTasksEnabled', Boolean(value))}
                        value={organizationKsyosAccount?.areContactsAndTasksEnabled}
                    />
                </FormGroup>
            );
        } else {
            return (
                <FormGroup
                    label={translateKey('organization.areContactsAndTasksEnabled')}
                    tooltipText={translateKey('organization.areContactsAndTasksEnabledTooltip')}
                >
                    <FormParagraph>
                        {areContactsAndTasksEnabled ? translateKey('common.yes') : translateKey('common.no')}
                    </FormParagraph>
                </FormGroup>
            );
        }
    }

    function renderStatus(isActiveInKsyosAccount: boolean) {
        const isEditMode = canEdit && isEditing;
        if (isEditMode && !organizationKsyosAccount?.isKsyos) {
            return (
                <InputRadioGroup
                    fieldKey="isActive"
                    label="common.status"
                    radioItems={[
                        { id: 'active', label: translateKey('common.isActive'), value: true },
                        { id: 'inactive', label: translateKey('common.archived'), value: false },
                    ]}
                />
            );
        } else {
            return (
                <FormGroup label={translateKey('common.status')}>
                    <FormParagraph>
                        {isActiveInKsyosAccount ? translateKey('common.isActive') : translateKey('common.archived')}
                    </FormParagraph>
                </FormGroup>
            );
        }
    }

    async function loadOrganizationKsyosAccount(organizationId: string) {
        const url = `/api/organizations/${organizationId}/ksyosAccount?includeItemAuthorizations=canUpdate,canUpdateProperties`;
        const result = await fetchJson(url, 'GET');
        setOriginalOrganizationKsyosAccount(result.json);
        setOrganizationKsyosAccount(result.json);
        setCanEdit(result.json.authorization.canUpdate?.allow);
    }

    function handleChange<K extends keyof IOrganizationKsyosAccount>(name: K, value: IOrganizationKsyosAccount[K]) {
        setOrganizationKsyosAccount({ ...organizationKsyosAccount, [name]: value } as IOrganizationKsyosAccount);
    }

    function validateOne(name: keyof IOrganizationKsyosAccount) {
        setErrors(
            updateValidationErrorsForOne(organizationKsyosAccountUpdateSchema, organizationKsyosAccount, errors, name)
        );
    }

    function handleCancel() {
        setOrganizationKsyosAccount(originalOrganizationKsyosAccount);
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    async function handleSubmit() {
        if (
            !showConfirmationModal &&
            originalOrganizationKsyosAccount?.isActive &&
            !organizationKsyosAccount?.isActive
        ) {
            setShowConfirmationModal(true);
        } else {
            if (organizationKsyosAccount) {
                setIsSaving(true);

                const filteredOrganizationKsyosAccount = {
                    authorization: { ...organizationKsyosAccount.authorization },
                    ...pick(organizationKsyosAccount, organizationKsyosAccount.authorization.canUpdateProperties!),
                };
                const { errors: validationErrors, item } = validate(
                    organizationKsyosAccountUpdateSchema,
                    filteredOrganizationKsyosAccount
                );

                if (validationErrors.size > 0) {
                    setErrors(validationErrors);
                } else {
                    const url = `/api/organizations/${props.organizationId}/ksyosAccount`;
                    await fetchJson(url, 'POST', item);
                    setOriginalOrganizationKsyosAccount(organizationKsyosAccount);
                    setErrors(emptyValidationErrors);
                    setIsEditing(false);
                    setShowConfirmationModal(false);
                }
            }
            setIsSaving(false);
        }
    }
}
